import { Card } from 'primereact/card';
import { Suspense, useEffect, useState, useTransition } from 'react';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { PreloadedQuery, useQueryLoader } from 'react-relay';
import { getWeeklySnapshotInfo } from '../../graphQLOperations/Dashboard';
import { useAppSelector } from '../../redux/hooks';
import {
  DashboardGetWeeklySnapshotQuery,
  WeeklyPaceSkillSource,
} from '../../graphQLOperations/__generated__/DashboardGetWeeklySnapshotQuery.graphql';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { selectUser } from '../../redux/reducers/userSlice';
import { Roles } from '../../models/User';
import TeacherDashboardInfoIcon, { InfoType } from './TeacherDashboardInfoIcon';
import { FilterOptionValues } from './TeacherDashboard';
import { WeeklyPaceContent } from './WeeklyPace/WeeklyPaceContent';
import { WeeklyPaceSourceToggle } from './WeeklyPace/WeeklyPaceSourceToggle';

export interface WeeklySnapshotQueryFilters {
  classIds: string[] | null;
  targetGroupIds: string[] | null;
  siteIds: string[] | null;
  gradeLevelIds: string[] | null;
  reportFromDate: Date;
  reportToDate: Date;
}

interface Props {
  queryFilters: WeeklySnapshotQueryFilters;
  filterOption: FilterOptionValues;
}

export const WeeklySnapshot = ({ queryFilters, filterOption }: Props) => {
  const userRedux = useAppSelector(selectUser);

  const [isPending, startTransition] = useTransition();
  const [source, setSource] = useState<WeeklyPaceSkillSource>('DEMONSTRATED');
  const startDate = startOfWeek(new Date(), { weekStartsOn: 1 });
  const endDate = endOfWeek(new Date(), { weekStartsOn: 1 });

  const [queryReferenceWeeklySnapshot, loadQueryWeeklySnapshot] =
    useQueryLoader<DashboardGetWeeklySnapshotQuery>(getWeeklySnapshotInfo);

  const isTeacher =
    userRedux?.roles.some(role => role.name.toLocaleLowerCase() === Roles.TEACHER) &&
    !userRedux?.roles.find(role => role.name.toLocaleLowerCase() === Roles.NETWORK_ADMIN) &&
    !userRedux?.roles.find(role => role.name.toLocaleLowerCase() === Roles.SITE_ADMIN);

  useEffect(() => {
    if (!isTeacher || (isTeacher && queryFilters.classIds)) {
      // Loading inside a transition keeps the previously rendered figures on screen while a new
      // source is fetched, so switching the toggle never blanks the card it lives in.
      startTransition(() => {
        loadQueryWeeklySnapshot({ filter: queryFilters, source }, { fetchPolicy: 'store-and-network' });
      });
    }
  }, [queryFilters, source]);

  return (
    <Card
      header={
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-border-x-0 tw-border-t-0 tw-border-b-2 tw-border-solid tw-border-neutral-100 tw-pb-3">
          <h2 className="tw-m-0 tw-font-sora tw-text-xl tw-font-bold tw-not-italic tw-text-neutral-950">Pace</h2>
          <p className="tw-m-0 tw-font-atkinson tw-text-base tw-text-neutral-600">
            {format(startDate, 'P')} - {format(endDate, 'P')}
          </p>
        </div>
      }
      // `!` throughout: the glass-light `.p-card` rule ships after the Tailwind layer, so at equal
      // specificity it wins over plain utilities. Border/radius/shadow here mirror the Alerts card.
      className="tw-h-full tw-w-full !tw-rounded-2xl !tw-border-2 !tw-border-solid !tw-border-neutral-100 !tw-p-5 !tw-shadow-none"
    >
      <div className={`tw-flex tw-w-full tw-flex-col tw-gap-4 ${isPending ? 'tw-opacity-60' : ''}`}>
        {queryReferenceWeeklySnapshot ? (
          <Suspense fallback={<ThreeDotLoader dark />}>
            <WeeklyPaceContent
              queryReference={
                queryReferenceWeeklySnapshot as PreloadedQuery<DashboardGetWeeklySnapshotQuery, Record<string, unknown>>
              }
              queryFilters={queryFilters}
              filterOption={filterOption}
              source={source}
              headerActions={
                <div className="tw-flex tw-items-center tw-gap-3">
                  <WeeklyPaceSourceToggle source={source} onChange={setSource} />
                  <TeacherDashboardInfoIcon infoType={InfoType.EVIDENCE_STANDARD} variant="plain" />
                </div>
              }
            />
          </Suspense>
        ) : (
          <ThreeDotLoader dark />
        )}
      </div>
    </Card>
  );
};
